import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { INTERVAL_PRESET_DAYS, addDays, formatDays } from './follow-up-schedule';

export const INTERVAL_DEFAULT = 'default';
const CUSTOM_PREFIX = 'custom:';

// Value format: 'default' | '<days>' | 'custom:<days>' (days may be empty while typing).
export function resolveIntervalDays(
  value: unknown,
  defaultDays: number | null
): number | null {
  const raw = String(value ?? '');
  if (raw === INTERVAL_DEFAULT) return defaultDays;
  const days = Number(raw.startsWith(CUSTOM_PREFIX) ? raw.slice(CUSTOM_PREFIX.length) : raw);
  return raw !== '' && days >= 1 && days <= 365 ? days : null;
}

/**
 * The "Repeat every" picker from the Programs drawer's schedule dialog, as a
 * standalone control so the clinician's consult dialog looks and works the same.
 */
@Component({
  selector: 'app-follow-up-interval-picker',
  standalone: true,
  imports: [CommonModule, MatFormFieldModule, MatInputModule, MatSelectModule],
  template: `
    <mat-form-field appearance="outline" class="cadence-field">
      <mat-label>Repeat every</mat-label>
      <mat-select [value]="choice" (selectionChange)="onChoice($event.value)">
        <mat-option [value]="DEFAULT">{{ defaultLabel }}</mat-option>
        @for (d of presets; track d) {
        <mat-option [value]="d">{{ formatDays(d) }}</mat-option>
        }
        <mat-option [value]="CUSTOM">Custom (days)…</mat-option>
      </mat-select>
    </mat-form-field>
    @if (choice === CUSTOM) {
    <mat-form-field appearance="outline" class="cadence-field">
      <mat-label>Custom interval (days)</mat-label>
      <input
        matInput
        type="number"
        min="1"
        max="365"
        [value]="customDays"
        (input)="onCustom($any($event.target).value)"
      />
      @if (customDays !== '' && !days) {
      <mat-hint class="schedule-invalid">Must be between 1 and 365 days</mat-hint>
      }
    </mat-form-field>
    } @if (days) {
    <div class="schedule-preview">
      <div class="preview-row">
        <span>This cycle ends</span>
        <strong>{{ thisCycleEnd | date : 'EEE d MMM y' }}</strong>
      </div>
      <div class="preview-row">
        <span>Next order created</span>
        <strong>{{ nextOrderDate | date : 'EEE d MMM y' }}</strong>
      </div>
      <div class="preview-row">
        <span>Next cycle runs</span>
        <strong
          >{{ thisCycleEnd | date : 'd MMM' }} – {{ nextCycleEnd | date : 'd MMM y' }}</strong
        >
      </div>
    </div>
    }
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .cadence-field {
        width: 100%;
        margin-top: 12px;
      }
      .schedule-preview {
        border: 1px solid #e0e0e0;
        border-radius: 6px;
        padding: 10px 14px;
        margin: 4px 0 12px;
        font-size: 13px;
      }
      .preview-row {
        display: flex;
        justify-content: space-between;
        padding: 3px 0;
      }
      .preview-row span {
        color: #6b7280;
      }
      .schedule-invalid {
        color: #b91c1c;
      }
    `,
  ],
})
export class FollowUpIntervalPickerComponent {
  readonly DEFAULT = INTERVAL_DEFAULT;
  readonly CUSTOM = 'custom';
  readonly presets = INTERVAL_PRESET_DAYS;
  readonly formatDays = formatDays;

  @Input() value: unknown = INTERVAL_DEFAULT;
  @Input() defaultDays: number | null = null;
  @Input() defaultLabelPrefix = 'Plan default';
  // Start of the cycle being consulted (a follow-up's existing start date);
  // null means it starts today. The consult sets this cycle's length.
  @Input() cycleStart: Date | null = null;
  @Input() leadDays: number | null = null;
  @Output() valueChange = new EventEmitter<string>();

  get choice(): string | number {
    const raw = String(this.value ?? INTERVAL_DEFAULT);
    if (raw === INTERVAL_DEFAULT) return INTERVAL_DEFAULT;
    if (raw.startsWith(CUSTOM_PREFIX)) return this.CUSTOM;
    return Number(raw);
  }

  get customDays(): string {
    const raw = String(this.value ?? '');
    return raw.startsWith(CUSTOM_PREFIX) ? raw.slice(CUSTOM_PREFIX.length) : '';
  }

  get defaultLabel(): string {
    return this.defaultDays
      ? `${this.defaultLabelPrefix} (${formatDays(this.defaultDays)})`
      : this.defaultLabelPrefix;
  }

  get days(): number | null {
    return resolveIntervalDays(this.value, this.defaultDays);
  }

  get thisCycleEnd(): Date | null {
    return this.days ? addDays(this.cycleStart ?? new Date(), this.days) : null;
  }

  get nextOrderDate(): Date | null {
    const end = this.thisCycleEnd;
    if (!end || !this.days) return null;
    return addDays(end, -Math.min(this.leadDays ?? 0, this.days - 1));
  }

  get nextCycleEnd(): Date | null {
    const end = this.thisCycleEnd;
    return end && this.days ? addDays(end, this.days) : null;
  }

  onChoice(choice: string | number): void {
    if (choice === this.CUSTOM) {
      this.valueChange.emit(CUSTOM_PREFIX + (this.days ?? ''));
    } else {
      this.valueChange.emit(String(choice));
    }
  }

  onCustom(raw: string): void {
    this.valueChange.emit(CUSTOM_PREFIX + (raw ?? ''));
  }
}
